import { memo, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ProductsModal from "src/components/modals/ProductsModal/ProductsModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { getBusinessTypeLabel, handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import {
  useCreateProcurementRequest,
  useDeleteVendor,
  useGetProducts,
  useGetVendors,
} from "../api-vendors";
import useVendorTableColumns from "./useVendorTableColumns";
import { VendorType } from "./vendor-types";

const VendorsList = () => {
  useSetActiveTab(TAB_NAMES.VENDOR_MANAGEMENT);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [vendorId, setVendorId] = useState<number | undefined>(undefined);
  const {
    isLoading,
    error,
    data,
    mutate: updateVendorData,
    isValidating,
  } = useGetVendors({ length, page, search: debouncedSearch });
  const { isRefreshing, handleRefresh } = useManualRefresh(updateVendorData);

  const getProducts = useGetProducts(vendorId);
  const createProcurementRequest = useCreateProcurementRequest(vendorId);

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deleteVendor,
  } = useDeleteVendor();

  const vendorData = useMemo(() => data?.data?.rows ?? [], [data?.data]);
  const mappedVendorsData = useMemo(() => {
    return vendorData?.map((vendor: VendorType) => {
      return {
        ...vendor,
        type: getBusinessTypeLabel(vendor.type),
      };
    });
  }, [vendorData]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const columns = useVendorTableColumns({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
    onToggleProducts: (id: number) => {
      setVendorId(id);
      setIsProductsModalOpen(true);
    },
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      updateVendorData();
      setIsDeleteModalOpen(false);
      successNotification("Vendor Deleted.");
    }
  }, [isDeleteSucceed, successNotification, updateVendorData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  return (
    <>
      <ProductsModal
        isOpen={isProductsModalOpen}
        onClose={() => setIsProductsModalOpen(false)}
        useGetData={() => getProducts}
        usePostData={() => createProcurementRequest}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteVendor(idToOperate)}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Vendor Management"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(vendorData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Add Vendor",
            onClick: () => navigate("create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={mappedVendorsData ?? []}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default memo(VendorsList);
